import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	createWorkspace,
	deleteWorkspace,
	getKnowledgeGraphs,
	getKnowledgeGraphVisualization,
	getWorkspaces,
	getWorkspaceVisualization,
	updateWorkspace,
} from "@/api/knowledgeGraphApi";
import ChatbotPanel from "@/features/chat/components/ChatbotPanel";
import CenterGraphPanel from "@/features/graph/components/CenterGraphPanel";
import type { FilterCriteria } from "@/features/graph/components/controls/FilterPanel";
import LeftPanelSwitcher from "@/features/notebook/components/LeftPanelSwitcher";
import NotebookHeader from "@/features/notebook/components/NotebookHeader";
import WorkspaceGrid from "@/features/notebook/components/WorkspaceGrid";
import useGraphMutations from "@/features/notebook/hooks/useGraphMutations";
import useGraphSelection from "@/features/notebook/hooks/useGraphSelection";
import useNotebookLayoutState from "@/features/notebook/hooks/useNotebookLayoutState";
import { useSupabaseSession } from "@/hooks/useSupabaseSession";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";
import { getMockKnowledgeGraphs } from "@/utils/mockKnowledgeGraphData";

const NotebookLayoutContainer: React.FC = () => {
	const { session, isLoading: isSessionLoading } = useSupabaseSession();
	const {
		selectedGraphId,
		setSelectedGraphId,
		selectedSources,
		graphStatistics,
		graphNodes,
		graphEdges,
		isEditingPanelVisible,
		setIsEditingPanelVisible,
		hasAutoSelectedRef,
		handleSourceToggle,
		handleSelectAll,
		handleStatisticsChange,
		handleNodesEdgesChange,
	} = useNotebookLayoutState();
	const { selectedNodeId, selectedEdgeId, isEditing, setSelectedNodeId } = useGraphSelection();

	const filterRef = useRef<((filter: FilterCriteria) => void) | null>(null);
	const resetFilterRef = useRef<(() => void) | null>(null);
	const deleteNodeRef = useRef<((nodeId: string) => void) | null>(null);

	const useMock =
		new URLSearchParams(window.location.search).get("mock") === "true" ||
		import.meta.env.VITE_USE_MOCK_GRAPH === "true";

	const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
	const queryClient = useQueryClient();

	const { data: workspacesData, isLoading: workspacesLoading } = useQuery({
		queryKey: ["workspaces"],
		queryFn: ({ signal }) => getWorkspaces({ signal }),
		enabled: !!session && !useMock,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	const { mutate: createWorkspaceMutate, isPending: createPending } = useMutation({
		mutationFn: (name: string) => createWorkspace(name),
		onSuccess: (newWs) => {
			queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			setSelectedWorkspaceId(newWs.id);
		},
	});

	const { mutate: renameWorkspaceMutate } = useMutation({
		mutationFn: ({ id, name }: { id: string; name: string }) => updateWorkspace(id, name),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workspaces"] }),
	});

	const { mutate: deleteWorkspaceMutate } = useMutation({
		mutationFn: (id: string) => deleteWorkspace(id),
		onSuccess: (_, id) => {
			if (selectedWorkspaceId === id) setSelectedWorkspaceId(null);
			queryClient.invalidateQueries({ queryKey: ["workspaces"] });
		},
	});

	const handleCreateWorkspace = useCallback(
		(name: string) => {
			createWorkspaceMutate(name);
		},
		[createWorkspaceMutate],
	);

	const handleRenameWorkspace = useCallback(
		(workspaceId: string) => {
			const ws = workspacesData?.find((w) => w.id === workspaceId);
			const newName = window.prompt("Rename workspace:", ws?.name ?? "");
			if (!newName || newName.trim() === "") return;
			renameWorkspaceMutate({ id: workspaceId, name: newName.trim() });
		},
		[workspacesData, renameWorkspaceMutate],
	);

	const handleDeleteWorkspace = useCallback(
		(workspaceId: string, name: string) => {
			if (!window.confirm(`Delete workspace "${name}"? This cannot be undone.`)) return;
			deleteWorkspaceMutate(workspaceId);
		},
		[deleteWorkspaceMutate],
	);

	const {
		data: graphsData,
		isLoading: graphsLoading,
		error: graphsError,
	} = useQuery({
		queryKey: ["knowledgeGraphs", useMock],
		queryFn: ({ signal }) => (useMock ? Promise.resolve(getMockKnowledgeGraphs()) : getKnowledgeGraphs({ signal })),
	});

	useEffect(() => {
		// Skip auto-selection — workspace view handles KG navigation.
		// Only restore the graph after a build job completes.
		if (!localStorage.getItem("graphBuildJobId") && !localStorage.getItem("chatGraphBuildJobId")) return;
		if (graphsData?.items && graphsData.items.length > 0 && !hasAutoSelectedRef.current) {
			setSelectedGraphId(graphsData.items[0].id);
			hasAutoSelectedRef.current = true;
		}
	}, [graphsData, hasAutoSelectedRef, setSelectedGraphId]);

	const { data: visualizationData } = useQuery({
		queryKey: ["knowledgeGraphVisualization", selectedGraphId],
		queryFn: ({ signal }) => getKnowledgeGraphVisualization(selectedGraphId as string, { signal }),
		enabled: !!selectedGraphId && !useMock,
		staleTime: 30_000,
	});

	useEffect(() => {
		if (!visualizationData) return;
		const nodes = (visualizationData.nodes ?? []) as unknown as VisualNode[];
		const edges = (visualizationData.edges ?? []) as unknown as VisualEdge[];
		handleNodesEdgesChange(nodes, edges);
		const n = nodes.length;
		const e = edges.length;
		handleStatisticsChange({
			nodes: n,
			edges: e,
			communities: 1,
			averageDegree: n > 0 ? Math.round(((2 * e) / n) * 10) / 10 : 0,
			density: n > 1 ? e / (n * (n - 1)) : 0,
		});
	}, [visualizationData, handleNodesEdgesChange, handleStatisticsChange]);

	const { data: workspaceVizData } = useQuery({
		queryKey: ["workspaceVisualization", selectedWorkspaceId],
		queryFn: ({ signal }) => getWorkspaceVisualization(selectedWorkspaceId as string, { signal }),
		enabled: !!selectedWorkspaceId && !selectedGraphId && !useMock,
		staleTime: 30_000,
	});

	useEffect(() => {
		if (selectedGraphId) return; // KG viz effect takes over when a KG is selected
		if (!workspaceVizData) return;
		const totalClasses = workspaceVizData.nodes.reduce((sum, kg) => sum + kg.classCount, 0);
		const n = workspaceVizData.nodes.length;
		const e = workspaceVizData.edges.length;
		const wsNodes: VisualNode[] = workspaceVizData.nodes.map((kg) => ({
			id: kg.id,
			graphElementId: kg.id,
			name: kg.name,
			elementType: "KnowledgeGraph",
			typeId: null,
			metadata: { classCount: kg.classCount, classLabels: kg.classLabels },
		}));
		const wsEdges: VisualEdge[] = workspaceVizData.edges.map((edge) => ({
			id: edge.id,
			predicateId: "",
			tupleId: edge.id,
			source: edge.source,
			target: edge.target,
			tuplePosition: 0,
			label: `${edge.sharedCount} shared classes`,
			inferred: false,
		}));
		handleNodesEdgesChange(wsNodes, wsEdges);
		handleStatisticsChange({
			nodes: n,
			edges: e,
			communities: n,
			averageDegree: n > 0 ? Math.round((totalClasses / n) * 10) / 10 : 0,
			density: n > 1 ? e / (n * (n - 1)) : 0,
		});
	}, [workspaceVizData, selectedGraphId, handleNodesEdgesChange, handleStatisticsChange]);

	const deleteNodeInGraph = useCallback((nodeId: string) => {
		if (deleteNodeRef.current) {
			deleteNodeRef.current(nodeId);
		}
	}, []);

	const { handleDeleteNode, handleGraphGenerated } = useGraphMutations({
		graphNodes,
		selectedNodeId,
		selectedGraphId,
		setSelectedGraphId,
		setSelectedNodeId,
		deleteNodeInGraph,
	});

	const handleFilterChange = useCallback((filter: FilterCriteria) => {
		if (filterRef.current) {
			filterRef.current(filter);
		}
	}, []);

	const handleResetFilter = useCallback(() => {
		if (resetFilterRef.current) {
			resetFilterRef.current();
		}
	}, []);

	// ── Render ──────────────────────────────────────────────────────────────────

	if (isSessionLoading) {
		return (
			<div className="flex items-center justify-center h-screen surface-alt">
				<div className="text-center">
					<Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
					<p className="text-muted">Checking session...</p>
				</div>
			</div>
		);
	}

	if (!session) {
		const next = encodeURIComponent(window.location.pathname + window.location.search);
		window.location.href = `/auth?next=${next}`;
		return null;
	}

	// Landing page: show workspace grid until the user selects a workspace
	if (!selectedWorkspaceId) {
		return (
			<WorkspaceGrid
				workspacesData={workspacesData}
				isLoading={workspacesLoading}
				onWorkspaceSelect={setSelectedWorkspaceId}
				onCreateWorkspace={handleCreateWorkspace}
				onRenameWorkspace={handleRenameWorkspace}
				onDeleteWorkspace={handleDeleteWorkspace}
				createPending={createPending}
			/>
		);
	}

	if (graphsLoading) {
		return (
			<div className="flex items-center justify-center h-screen surface-alt">
				<div className="text-center">
					<Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
					<p className="text-muted">Loading knowledge graphs...</p>
				</div>
			</div>
		);
	}

	if (graphsError) {
		return (
			<div className="flex items-center justify-center h-screen surface-alt">
				<div className="text-center">
					<AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
					<h2 className="text-xl font-semibold text-primary mb-2">Error Loading Graphs</h2>
					<p className="text-muted mb-4">
						{graphsError instanceof Error ? graphsError.message : "Failed to load knowledge graphs"}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="h-screen flex flex-col surface-alt text-primary">
			<NotebookHeader
				workspaceName={workspacesData?.find((w) => w.id === selectedWorkspaceId)?.name ?? "My Workspace"}
				onBackToGrid={() => {
					setSelectedWorkspaceId(null);
					setSelectedGraphId(null);
				}}
			/>

			<div className="flex-1 flex overflow-hidden">
				<LeftPanelSwitcher
					isEditing={isEditing}
					selectedNode={(graphNodes ?? []).find((node) => node.id === selectedNodeId) || null}
					isEditingPanelVisible={isEditingPanelVisible}
					onToggleEditingPanel={() => setIsEditingPanelVisible((prev) => !prev)}
					onDeleteNode={handleDeleteNode}
					graphId={selectedGraphId}
					graphNodes={graphNodes ?? []}
					graphEdges={graphEdges ?? []}
					selectedSources={selectedSources}
					onSourceToggle={handleSourceToggle}
					onSelectAll={handleSelectAll}
					onGraphGenerated={handleGraphGenerated}
					workspaceId={selectedWorkspaceId}
					graphStatistics={graphStatistics}
					selectedNodeId={selectedNodeId}
					selectedEdgeId={selectedEdgeId}
					onFilter={handleFilterChange}
					onResetFilter={handleResetFilter}
				/>

				<CenterGraphPanel
					onKgSelect={setSelectedGraphId}
					onNodeSelect={setSelectedNodeId}
					workspaceId={selectedWorkspaceId}
					expanded={false}
				/>

				<div className="right-panel">
					<div className="right-panel-content">
						<ChatbotPanel
							workspaceId={selectedWorkspaceId}
							selectedDocumentIds={Array.from(selectedSources)}
							selectedGraphId={selectedGraphId}
							onGraphGenerated={handleGraphGenerated}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default NotebookLayoutContainer;
